import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, Plus, CheckCircle2, Circle, ArrowLeft, ScanLine } from "lucide-react";
import camApi from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";
import { PickerSelect } from "@/components/cam/AssetDrawer";

export default function Audits() {
  const { can } = useCam();
  const [openId, setOpenId] = useState(null);
  if (openId) return <AuditDetail id={openId} onBack={() => setOpenId(null)} canVerify={can.admin} />;
  return <AuditList onOpen={setOpenId} canCreate={can.admin} />;
}

function AuditList({ onOpen, canCreate }) {
  const { tree } = useCam();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = async () => { setLoading(true); try { const r = await camApi.audits(); setRows(r.audits || []); } catch (e) { toast.error(e.message); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-cobalt-500" /><div className="overline">Audit cycles</div></div>
        {canCreate && <Button size="sm" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New audit</Button>}
      </div>
      {loading ? <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No audits yet. Start one to verify assets by scanning their labels.</div>
          : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                <th className="px-4 py-2.5">Audit</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 hidden sm:table-cell">Started</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className="border-b last:border-0 hover:bg-black/[0.02] cursor-pointer" onClick={() => onOpen(a.id)}>
                    <td className="px-4 py-2.5 font-medium">{a.name}</td>
                    <td className="px-4 py-2.5"><span className={`text-xs font-semibold ${a.status === "completed" ? "text-green-600" : "text-amber-600"}`}>{a.status === "completed" ? "Completed" : "Open"}</span></td>
                    <td className="px-4 py-2.5 hidden sm:table-cell text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-cobalt-500">Open →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      {open && <NewAuditDialog tree={tree} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function NewAuditDialog({ tree, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", scope: "all", scope_lab_id: "", scope_room_id: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required."); return; }
    setBusy(true);
    try {
      const body = { name: f.name.trim() };
      if (f.scope === "lab" && f.scope_lab_id) body.scope_lab_id = Number(f.scope_lab_id);
      if (f.scope === "room" && f.scope_room_id) body.scope_room_id = Number(f.scope_room_id);
      const r = await camApi.createAudit(body);
      toast.success(`Audit created — ${r.items} assets to verify`); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tighter">New audit cycle</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Q3 CSE Block audit" /></div>
          <div><Label className="text-xs">Scope</Label>
            <PickerSelect value={f.scope} onChange={(v) => setF({ ...f, scope: v })} options={[{ value: "all", label: "All assets" }, { value: "lab", label: "One lab" }, { value: "room", label: "One room" }]} className="mt-1" />
          </div>
          {f.scope === "lab" && <div><Label className="text-xs">Lab</Label><PickerSelect placeholder="Select lab" value={f.scope_lab_id} onChange={(v) => setF({ ...f, scope_lab_id: v })} options={tree.labs.map((l) => ({ value: String(l.id), label: l.name }))} className="mt-1" /></div>}
          {f.scope === "room" && <div><Label className="text-xs">Room</Label><PickerSelect placeholder="Select room" value={f.scope_room_id} onChange={(v) => setF({ ...f, scope_room_id: v })} options={tree.rooms.map((r) => ({ value: String(r.id), label: r.name }))} className="mt-1" /></div>}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={save} disabled={busy}>{busy ? "Creating…" : "Start audit"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AuditDetail({ id, onBack, canVerify }) {
  const [audit, setAudit] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tag, setTag] = useState("");
  const load = async () => { setLoading(true); try { const r = await camApi.audit(id); setAudit(r.audit); setItems(r.items || []); } catch (e) { toast.error(e.message); } finally { setLoading(false); } };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const verifiedCount = items.filter((i) => i.verified).length;
  const verifyByTag = async () => {
    if (!tag.trim()) return;
    try { await camApi.verifyAudit(id, { asset_tag: tag.trim() }); setTag(""); toast.success("Verified"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const verifyItem = async (assetId) => { try { await camApi.verifyAudit(id, { asset_id: assetId }); load(); } catch (e) { toast.error(e.message); } };
  const complete = async () => { if (!window.confirm("Close this audit? Unverified assets will be flagged missing.")) return; try { await camApi.completeAudit(id); toast.success("Audit completed"); load(); } catch (e) { toast.error(e.message); } };

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />All audits</button>
      {loading ? <div className="glass rounded-2xl p-8 text-center text-sm text-muted-foreground">Loading…</div> : (
        <>
          <div className="glass rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-xl tracking-tighter">{audit?.name}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{verifiedCount} of {items.length} verified{audit?.status === "completed" ? " · completed" : ""}</div>
              </div>
              {canVerify && audit?.status !== "completed" && <Button variant="outline" onClick={complete}><CheckCircle2 className="h-4 w-4 mr-1.5" />Complete</Button>}
            </div>
            <div className="mt-3 h-2 rounded-full bg-black/10 overflow-hidden"><div className="h-full bg-green-500 transition-all" style={{ width: `${items.length ? (verifiedCount / items.length) * 100 : 0}%` }} /></div>
            {canVerify && audit?.status !== "completed" && (
              <div className="flex gap-2 mt-4">
                <div className="relative flex-1"><ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input className="pl-9 text-mono text-sm" placeholder="Scan or type a tag to verify…" value={tag} onChange={(e) => setTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verifyByTag()} /></div>
                <Button onClick={verifyByTag}>Verify</Button>
              </div>
            )}
          </div>

          <div className="glass rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b last:border-0">
                    <td className="px-4 py-2.5 w-8">{it.verified ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Circle className="h-4 w-4 text-muted-foreground/40" />}</td>
                    <td className="px-4 py-2.5"><div className="font-medium">{it.asset?.name}</div><div className="text-mono text-[11px] text-muted-foreground">{it.asset?.asset_tag}</div></td>
                    <td className="px-4 py-2.5 text-right">
                      {!it.verified && canVerify && audit?.status !== "completed"
                        ? <button onClick={() => verifyItem(it.asset_id)} className="text-xs text-cobalt-500 hover:underline">Mark verified</button>
                        : it.verified ? <span className="text-[11px] text-muted-foreground">{it.verified_at ? new Date(it.verified_at).toLocaleDateString() : ""}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
